import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { paperProps } from '../../../constants';
import { USERS_URL } from '../../../constants';
import { deleteStopApi, getStopApi, postStopApi } from '../../../services/StopApi/StopApi';
import {
  getDialogHeading,
} from '../../common/commonUtils/commonUtils';
import moment from 'moment';

const isNotFoundError = (error) =>
  error === '{"error":"User not found"}' ||
  error === '{"message":"User not found"}' ||
  error?.includes?.('404') ||
  error?.includes?.('not found') ||
  error?.includes?.('Not found');

const ProviderLoginDialog = ({ open, setOpen, email, cntId, authToken }) => {
  const [loading, setLoading] = useState(false);
  const [loginDetails, setLoginDetails] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const fetchLoginDetails = () => {
    setLoading(true);
    setError(null);
    setLoginDetails(null);
    getStopApi({
      commonParams: `${USERS_URL}/${encodeURIComponent(email)}`,
      authorization: authToken,
    })
      .then((data) => {
        setLoginDetails(data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (open && email) {
      fetchLoginDetails();
    }
  }, [open, email, authToken]);

  const handleCreateLogin = () => {
    setCreating(true);
    postStopApi({
      url: USERS_URL,
      payload: { email, userId: cntId },
      authorization: authToken,
    })
      .then(() => {
        setCreating(false);
        fetchLoginDetails();
      })
      .catch((e) => {
        setCreating(false);
        setError(e);
      });
  };

  const handleDeleteLogin = () => {
    if (!window.confirm('Are you sure you want to remove this provider login? This action cannot be undone.')) {
      return;
    }
    setDeleting(true);
    setError(null);
    deleteStopApi({
      url: `${USERS_URL}/${encodeURIComponent(email)}`,
      authorization: authToken,
    })
      .then(() => {
        setDeleting(false);
        setLoginDetails(null);
        setDeleteSuccess(true);
        fetchLoginDetails();
      })
      .catch((e) => {
        setDeleting(false);
        setError(e);
      });
  };

  const handleClose = () => {
    setOpen(false);
    setLoginDetails(null);
    setError(null);
  };

  return (
    <>
    <Dialog maxWidth={'sm'} open={open} onClose={handleClose} >
      {getDialogHeading('Provider Login Details', email, handleClose)}
      <Grid container spacing={2} sx={{ p: 2 }}>
        {(loading || creating || deleting) && (
          <Grid item xs={12} sx={{ textAlign: 'center' }}>
            <CircularProgress />
          </Grid>
        )}
        {error && !loading && !creating && !deleting && (
          <>
            {isNotFoundError(error) ? (
              <>
                <Grid item xs={12}>
                  <Typography>No provider login found for this email.</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant='contained'
                    onClick={handleCreateLogin}
                  >
                    Create Provider Login
                  </Button>
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Typography color="error">Error: {error}</Typography>
              </Grid>
            )}
          </>
        )}
        {!loading && !creating && !deleting && !error && loginDetails && (
          <>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Status</Typography>
              <Typography variant="body1">{loginDetails.userStatus}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Enabled</Typography>
              <Typography variant="body1">{loginDetails.enabled ? 'Yes' : 'No'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Email Verified</Typography>
              <Typography variant="body1">{loginDetails.emailVerified === 'True' ? 'Yes' : 'No'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">User ID</Typography>
              <Typography variant="body1">{loginDetails.userId}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Created</Typography>
              <Typography variant="body1">{loginDetails.userCreateDate ? moment(loginDetails.userCreateDate).format('MM/DD/YYYY') : ''}</Typography>
            </Grid>
            {/*<Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Last Modified</Typography>
              <Typography variant="body1">{loginDetails.userLastModifiedDate ? moment(loginDetails.userLastModifiedDate).format('MM/DD/YYYY') : ''}</Typography>
            </Grid>*/}
            <Grid item xs={12}>
              <Button
                variant='contained'
                color='error'
                onClick={handleDeleteLogin}
              >
                Remove Provider Login
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </Dialog>
    <Snackbar
      open={deleteSuccess}
      autoHideDuration={4000}
      onClose={() => setDeleteSuccess(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={() => setDeleteSuccess(false)} severity="success" variant="filled">
        Provider login has been removed.
      </Alert>
    </Snackbar>
    </>
  );
};

export default ProviderLoginDialog;
